import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AwaitContainer } from '@ibsys/kit';
import { PageContainer } from '@/shared/ui';
import { StrategistFilters } from '@/widgets/StrategistFilters';
import type { StrategistFiltersValue } from '@/widgets/StrategistFilters';
import {
  defaultThreshold,
  isThresholdApplied,
  STRATEGIST_CONCEPT_ROLE,
  useStrategistStandardsMatrix,
} from '@/entities/strategist';
import type {
  StrategistRowRole,
  StrategistStandardsMatrixRequest,
  StrategistTargetAttribute,
} from '@/entities/strategist';
import { ConceptCardDrawer, useOpenConceptCard } from '@/features/concept-card';
import { DialogsDrawerContent, useOpenDialogsDrawer } from '@/features/dialogs-drawer';
import { StrategistStandardsControls } from './StrategistStandardsControls';
import { StrategistStandardsViz } from './StrategistStandardsViz';

/** Число строк матрицы стандартов по умолчанию. */
const DEFAULT_TOP_ROWS = 15;

/** Число столбцов-блоков по умолчанию. */
const DEFAULT_TOP_COLS = 12;

/**
 * Страница «Стандарты» раздела «Стратег»: тепловая матрица «понятие × блок
 * стандарта». Держит состояние сквозного фильтра, роли строк, целевой метрики
 * и размера матрицы, запрашивает данные (`useStrategistStandardsMatrix`) и
 * рендерит визуализацию.
 */
export const StrategistStandardsPage: FC = () => {
  const { t } = useTranslation(['common', 'strategist']);
  const [filterValue, setFilterValue] = useState<StrategistFiltersValue>({ filters: [] });
  const [rowRole, setRowRole] = useState<StrategistRowRole>(STRATEGIST_CONCEPT_ROLE.objection);
  const [target, setTarget] = useState<StrategistTargetAttribute>({ retention: true });
  const [topRows, setTopRows] = useState<number>(DEFAULT_TOP_ROWS);
  const [topCols, setTopCols] = useState<number>(DEFAULT_TOP_COLS);
  // Порог близости строк (по роли строк); инициализируется из первого ответа.
  const [threshold, setThreshold] = useState<number | undefined>(undefined);

  const body = useMemo<StrategistStandardsMatrixRequest>(
    () => ({
      filters: filterValue.filters,
      dateFrom: filterValue.dateFrom,
      dateTo: filterValue.dateTo,
      targetAttribute: target,
      rowRole: rowRole,
      topRows: topRows,
      topCols: topCols,
      threshold,
    }),
    [filterValue, target, rowRole, topRows, topCols, threshold],
  );

  const { data, isLoading, isPlaceholderData } = useStrategistStandardsMatrix(body);

  // Показывать можно только данные, посчитанные теми же параметрами, что уйдут в срез ячейки:
  // пока идёт запрос за новыми или порог ещё не подставлен, матрица и панель диалогов разойдутся.
  const isAwaitingData = isLoading || isPlaceholderData || !isThresholdApplied(data?.thresholds, [threshold]);

  // Порог берётся из первого ответа: доступные значения известны только по нему.
  useEffect(() => {
    if (!data) return;
    setThreshold((prev) => prev ?? defaultThreshold(data.thresholds));
  }, [data]);

  const handleConceptOpen = useOpenConceptCard(filterValue);

  const openDialogs = useOpenDialogsDrawer({ ...filterValue, target });
  const handleCellOpen = useCallback(
    (params: { objectionId: string; block: string; title: string }) => {
      // Строка матрицы — понятие роли строк, и раскрывается оно тем же порогом, что и матрица.
      openDialogs({ ...params, objectionThreshold: threshold });
    },
    [openDialogs, threshold],
  );

  return (
    <PageContainer className="flex flex-col gap-5">
      <h1 className="text-xl font-semibold text-foreground">{t('common:routes.strategistStandards')}</h1>
      <StrategistFilters value={filterValue} onChange={setFilterValue} />
      <StrategistStandardsControls
        rowRole={rowRole}
        onRowRoleChange={setRowRole}
        target={target}
        onTargetChange={setTarget}
        topRows={topRows}
        onTopRowsChange={setTopRows}
        topCols={topCols}
        onTopColsChange={setTopCols}
        thresholds={data?.thresholds ?? []}
        threshold={threshold}
        onThresholdChange={setThreshold}
      />
      <div className="flex-1 min-h-0">
        <AwaitContainer isLoading={isAwaitingData}>
          {!data || data.rows.length === 0 || data.columns.length === 0 ? (
            <div className="flex h-40 items-center justify-center rounded-xl border border-border bg-card text-sm text-muted-foreground">
              {t('common:emptyTable')}
            </div>
          ) : (
            <StrategistStandardsViz data={data} onConceptOpen={handleConceptOpen} onCellOpen={handleCellOpen} />
          )}
        </AwaitContainer>
      </div>
      <ConceptCardDrawer />
      <DialogsDrawerContent />
    </PageContainer>
  );
};
